import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import toast from 'react-hot-toast';
import api, { setCognitiveFlowsApiBase } from '../cognitive-flows/utils/api';
import CanvasBlockCard from '../cognitive-flows/components/canvas/CanvasBlockCard';
import { downloadCanvasExcel, printCanvasPdf } from '../cognitive-flows/components/canvas/canvasExport';
import { ui, btnGhost, PLAN_ROLES } from '../cognitive-flows/components/canvas/canvasUi';
import { IconDownload, IconChevronLeft } from '../cognitive-flows/components/canvas/icons';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import '../cognitive-flows/components/canvas/canvas.css';

const Grid = WidthProvider(GridLayout);

/**
 * A canvas opened from a share link (/canvas-share/:token): read-only grid
 * of the blocks the token grants — full canvas for allowlist links, the
 * redacted copy for public links. Signed-out visitors reach this page via
 * CanvasShareGate → Login → ShareReturn.
 */

const s = {
    // Standalone, full-viewport: App renders this page OUTSIDE the signed-in
    // grid shell (no sidebar/header — another account's data must never sit
    // under this account's navigation), so the page owns the whole viewport.
    page: { height: '100vh', width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', background: ui.paper },
    head: { flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: ui.surface, borderBottom: ui.border },
    title: { fontSize: '18px', fontWeight: 750, color: ui.ink, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    sharedBy: { fontSize: '11.5px', color: ui.ink2, fontWeight: 600 },
    menuWrap: { position: 'relative', display: 'inline-flex', marginLeft: 'auto' },
    menu: { position: 'absolute', right: 0, top: '38px', zIndex: 40, background: ui.surface, border: ui.border, borderRadius: ui.radius, boxShadow: ui.shadowOverlay, minWidth: '170px', overflow: 'hidden' },
    menuItem: { display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', padding: '10px 14px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit', color: ui.ink },
    gridWrap: { flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 },
    center: { minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', color: ui.ink2, fontSize: '14px', textAlign: 'center', padding: '24px' },
    centerTitle: { fontSize: '17px', fontWeight: 750, color: ui.ink },
};

export default function CanvasSharePage({ app_url }) {
    const { token } = useParams();
    const navigate = useNavigate();
    const [canvas, setCanvas] = useState(null);
    const [state, setState] = useState('loading'); // loading | ready | gone | needsStore
    const [exportOpen, setExportOpen] = useState(false);
    const gridWrapRef = useRef(null);

    useEffect(() => {
        // Cold load (the email-link path): nothing else has configured the
        // API base yet — the relative default is wrong on split-origin
        // deployments. Mirrors ShareAcceptPage.
        if (app_url) setCognitiveFlowsApiBase(app_url);
        let live = true;
        api.get(`/canvases/shared/token/${encodeURIComponent(token)}`)
            .then(({ data }) => { if (live) { setCanvas(data); setState('ready'); } })
            .catch((err) => {
                if (!live) return;
                setState(err.response?.data?.code === 'SHOP_REQUIRED' ? 'needsStore' : 'gone');
            });
        return () => { live = false; };
    }, [token, app_url]);

    const layout = useMemo(
        () => (canvas?.blocks || []).map((b) => ({ i: b.id, x: b.layout.x, y: b.layout.y, w: b.layout.w, h: b.layout.h, static: true })),
        [canvas],
    );

    // Role eyebrows for a chat-built canvas. A PUBLIC link never carries
    // `origin` (sanitizeCanvasForPublic is a whitelist), so this is empty for
    // it; an allowlist link sees the full doc and gets the eyebrows.
    const roleById = useMemo(() => {
        const map = {};
        for (const pb of canvas?.origin?.plan?.blocks || []) {
            if (pb?.blockId && PLAN_ROLES[pb.role]) map[pb.blockId] = { label: PLAN_ROLES[pb.role].label, hue: PLAN_ROLES[pb.role].hue };
        }
        return map;
    }, [canvas]);

    const exportBlocks = () => [...(canvas?.blocks || [])].sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);
    const exportExcel = () => {
        setExportOpen(false);
        if (!downloadCanvasExcel(canvas.name, exportBlocks())) toast.error('Nothing to export yet');
    };
    const exportPdf = async () => {
        setExportOpen(false);
        if ((canvas?.blocks || []).length === 0) { toast.error('Nothing to export yet'); return; }
        // No canvasId to fetch images through here (this is the share-token
        // surface, not the owner one) — printCanvasPdf simply omits image
        // blocks from the printed page, same as any other export failure mode.
        if (!(await printCanvasPdf(canvas.name, exportBlocks(), gridWrapRef.current))) toast.error('Allow pop-ups to export the PDF');
    };

    if (state === 'loading') {
        return <div style={s.page}><div style={s.center}>Loading…</div></div>;
    }
    if (state === 'gone') {
        return (
            <div style={s.page}>
                <div style={s.center}>
                    <span style={s.centerTitle}>This link is no longer available.</span>
                    <span>It may have expired or been removed by the owner.</span>
                    <button type="button" className="cvx-ghost cvx-focus" style={btnGhost} onClick={() => navigate('/', { replace: true })}>Go to the app</button>
                </div>
            </div>
        );
    }
    if (state === 'needsStore') {
        return (
            <div style={s.page}>
                <div style={s.center}>
                    <span style={s.centerTitle}>Finish setting up your account first.</span>
                    <span>Complete store setup, then open this link again to view the shared dashboard.</span>
                    <button type="button" className="cvx-ghost cvx-focus" style={btnGhost} onClick={() => navigate('/setup-store', { replace: true })}>Complete setup</button>
                </div>
            </div>
        );
    }

    return (
        <div style={s.page}>
            <div style={s.head}>
                <button type="button" className="cvx-iconbtn cvx-focus" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: ui.radiusSm, cursor: 'pointer', padding: 0 }} onClick={() => navigate('/', { replace: true })} title="Go to the app" aria-label="Go to the app"><IconChevronLeft size={17} /></button>
                <span>
                    <h1 style={s.title}>{canvas.name}</h1>
                    <div style={s.sharedBy}>
                        Shared by {canvas.createdBy || 'the owner'} · view only
                        {canvas.sourceAccountName && (
                            <> · From {canvas.sourceAccountName}{canvas.sourceWorkspaceName ? ` / ${canvas.sourceWorkspaceName}` : ''}</>
                        )}
                    </div>
                </span>
                <span style={s.menuWrap}>
                    <button type="button" className="cvx-ghost cvx-focus" style={btnGhost} onClick={() => setExportOpen((o) => !o)} title="Export this canvas"><IconDownload size={14} /> Export</button>
                    {exportOpen && (
                        <div style={s.menu}>
                            <button type="button" className="cvx-menu-item cvx-focus" style={s.menuItem} onClick={exportExcel}>Excel workbook</button>
                            <button type="button" className="cvx-menu-item cvx-focus" style={s.menuItem} onClick={exportPdf}>PDF (print)</button>
                        </div>
                    )}
                </span>
            </div>
            <div ref={gridWrapRef} style={s.gridWrap}>
                {(canvas.blocks || []).length === 0 ? (
                    <div style={s.center}>This canvas is empty.</div>
                ) : (
                    <Grid cols={12} rowHeight={40} margin={[12, 12]} layout={layout}
                        isDraggable={false} isResizable={false} compactType="vertical">
                        {(canvas.blocks || []).map((b) => (
                            <div key={b.id} data-block-id={b.id}>
                                <CanvasBlockCard block={b} shareToken={token} running={false} readOnly eyebrow={roleById[b.id] || null} />
                            </div>
                        ))}
                    </Grid>
                )}
            </div>
        </div>
    );
}
